import base64 from 'base-64';
import axios, { type AxiosResponse } from 'axios';
import { localStorage } from 'shared/utils';
import { LocalStorageKey } from 'shared/types';
import { requestLoginRedirect } from 'routing/auth/auth-redirect';

import { LOGOUT } from '../actions/actions';
import { URL_WHITELIST } from '../utils/constants';
import { getRequestHeaders } from '../utils/request-headers';
import { logout } from '../requireAuth';

import {
  isLegacyApiAction,
  type LegacyAction,
  type LegacyDispatch,
  type LegacyMiddlewareStore,
} from './api-middleware.types';
import { STATUS_META, notify, dispatchAction, nextAction } from './utils';

interface LegacyErrorResponseData {
  [key: string]: unknown;
  details?: unknown;
}

interface LegacyApiError {
  response?: AxiosResponse<LegacyErrorResponseData>;
}

const decodeSession = (token: string): unknown => {
  const payload: unknown = JSON.parse(base64.decode(token.split('.')[1]));

  if (payload === null) {
    throw new TypeError("Cannot read properties of null (reading 'session')");
  }

  return Reflect.get(Object(payload), 'session');
};

const apiMiddleware =
  (store: LegacyMiddlewareStore) =>
  (next: LegacyDispatch) =>
  (action: LegacyAction): unknown => {
    // if user logs out
    if (action.type === LOGOUT) {
      logout(requestLoginRedirect);
      return next(action);
    }

    // if action is not an api call
    if (!isLegacyApiAction(action)) {
      return next(action);
    }

    // if action is an api call dispatch a loading action while api call is loading
    const { type, meta, ...loadingAction } = action;
    void type;
    void meta;

    store.dispatch({ type: `${action.type}_LOADING`, ...loadingAction });

    // make the api call
    axios({
      method: `${action.meta.method}`,
      url: `${process.env.REACT_APP_API_URL}${action.meta.url}`,
      headers: getRequestHeaders(),
      params: action.meta.params, // for getting data
      data: action.meta.data, // for posting data
    })
      .then((response) => {
        let requestWasHandled = false;
        const {
          headers: { authorization },
          config: { url: requestUrl },
        } = response;
        if (URL_WHITELIST.some((path) => requestUrl!.indexOf(path) >= 0)) {
          nextAction(next)(action)(response);
          requestWasHandled = true;
        }

        if (localStorage.getItem(LocalStorageKey.TOKEN)) {
          if (
            typeof authorization === 'string' &&
            decodeSession(authorization) ===
              localStorage.getItem(LocalStorageKey.SESSION_ID)
          ) {
            localStorage.setItem(LocalStorageKey.TOKEN, authorization);
          }

          nextAction(next)(action)(response);
          requestWasHandled = true;
        }

        if (requestWasHandled) action.meta.onSuccess?.();
      })
      .catch((error: LegacyApiError) => {
        const response = error?.response;
        const status = response?.status;
        const details = response?.data?.details;

        // Show error in the console
        console.error('The was a problem with API call.', error);

        if (status !== 406) {
          // Notify user about error in frontend
          notify(error);
        }

        const token = localStorage.getItem(LocalStorageKey.TOKEN);
        if (!token) {
          return;
        }

        const legacySessionComparison = Object.is(
          !decodeSession(token),
          localStorage.getItem(LocalStorageKey.SESSION_ID),
        );
        if (legacySessionComparison) {
          return;
        }

        if (!error || !response) {
          const meta = {
            status: '500',
            msg: `Unhandled errorCode ${JSON.stringify(
              error,
            )}. Please try again in a few minutes.`,
          };

          dispatchAction(store, action, meta);
          return;
        }

        switch (status) {
          case 403:
          case 401:
            store.dispatch({ type: LOGOUT });
            break;
          case 500:
            dispatchAction(store, action, STATUS_META(details)[500]);
            break;
          case 400:
            dispatchAction(store, action, STATUS_META()[400]);
            break;
          case 404:
            dispatchAction(store, action, STATUS_META()[404]);
            break;
          case 406:
            dispatchAction(
              store,
              { ...action, ...response.data },
              STATUS_META(details)[406],
            );
            break;
          default:
            break;
        }
      })
      .finally(() => action.meta.onSettled?.());
  };

export default apiMiddleware;
